import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { formatCurrencySGD } from "utils/formatNumber";
import { formatDateTimeShort } from "utils/formatDate";
import ItemShop from "./ItemShop";
import CheckoutApi from "api/checkout.api";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { clearCart, getCartOther } from "redux/modules/cart";
import { GetOrdersByUser } from "redux/modules/order";
import { PATHS } from "routes/paths";

const OmisePayment = ({ open, setOpen, data, user, delivery, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  // Generate a unique transaction reference
  const generateTransactionRef = () => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 10000);
    return `alco-${timestamp}-${random}`;
  };

  // Get the current domain for callback URL
  const domain = window.location.origin;
  const callbackUrl = `${domain}${PATHS.checkout.omiseCallback}`;

  useEffect(() => {
    // Load Omise.js script when component mounts or when dialog opens
    if (open) {
      const loadOmiseScript = () => {
        const existingScript = document.getElementById("omise-script");
        if (!existingScript) {
          const script = document.createElement("script");
          script.src = "https://cdn.omise.co/omise.js";
          script.id = "omise-script";
          script.async = true;
          document.body.appendChild(script);
        }
      };

      loadOmiseScript();
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Following workflow.yaml configuration
      if (!window.Omise) {
        toast.error("Payment system is not ready yet. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
        });
        setLoading(false);
        return;
      }

      // Generate a transaction reference for this payment attempt
      const transactionRef = generateTransactionRef();

      // Include the reference in the callback URL as per workflow.yaml recommendation
      const callbackUrlWithRef = `${callbackUrl}?ref=${transactionRef}`;

      // Initialize Omise with public key as shown in workflow.yaml
      window.Omise.setPublicKey(
        import.meta.env.VITE_OMISE_PUBLIC_KEY || "pkey_test_your_public_key"
      );

      // Use Omise Card UI instead of directly working with tokens
      window.OmiseCard = window.OmiseCard || {};
      window.OmiseCard.configure({
        publicKey:
          import.meta.env.VITE_OMISE_PUBLIC_KEY || "pkey_test_your_public_key",
        currency: "sgd",
        frameLabel: "AlcoVault",
        submitLabel: "Pay Now",
        buttonLabel: "Pay with Credit Card",
      });

      window.OmiseCard.open({
        amount: Math.round(data.total * 100), // Convert to smallest currency unit (cents)
        frameDescription: `Order #${new Date().getTime()}`,
        defaultPaymentMethod: "credit_card",
        onCreateTokenSuccess: async (token) => {
          // Send token to backend as described in workflow.yaml
          try {
            const payload = {
              token: token,
              total: data.total,
              user: user?._id,
              delivery,
              drams: data.drams,
              bottles: data.bottles,
              subtotal: data.subtotal,
              totalShiping: data.totalShiping,
              insurance: data.insurance || 0,
              buyerFee: data.buyerFee || 0,
              paymentFee: data.paymentFee || 0,
              point: data.point || 0,
              wallet: data.wallet || 0,
              selectShipping: data.selectShipping,
              country: data.country,
              description: "AlcoVault Purchase",
              return_uri: callbackUrlWithRef, // Add return_uri with transaction reference
              transaction_ref: transactionRef, // Include the transaction reference in the payload
            };

            const response = await CheckoutApi.paymentOmise(payload);

            // Handle the payment result as per workflow
            if (response.status === "successful") {
              toast.success("Payment successful!", {
                position: toast.POSITION.TOP_CENTER,
              });

              if (user) {
                dispatch(GetOrdersByUser(user?._id));
              }
              dispatch(clearCart());
              dispatch(getCartOther());

              onSuccess();
            } else if (
              response.status === "pending" &&
              response.requires_action
            ) {
              // Handle 3D Secure authentication if required
              // Instead of navigating directly, show user a message first
              toast.info("Redirecting to secure authentication...", {
                position: toast.POSITION.TOP_CENTER,
              });

              // Slight delay to allow toast to show before redirect
              setTimeout(() => {
                window.location.href = response.charge.authorize_uri;
              }, 1000);
            } else {
              toast.error(
                response.message || "Payment failed. Please try again.",
                {
                  position: toast.POSITION.TOP_CENTER,
                }
              );
            }
          } catch (error) {
            console.error("API error:", error);
            toast.error("Payment processing error. Please try again.", {
              position: toast.POSITION.TOP_CENTER,
            });
          }
          setLoading(false);
          setOpen(false);
        },
        onFormClosed: () => {
          setLoading(false);
        },
      });
    } catch (error) {
      setLoading(false);
      toast.error("Error processing payment. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
      console.error("Omise payment error:", error);
    }
  };

  return (
    <Dialog
      header="Omise Payment"
      visible={open}
      onHide={() => setOpen(false)}
      style={{ width: "70vw" }}
      breakpoints={{
        "1400px": { width: "70vw" },
        "960px": { width: "75vw" },
        "641px": { width: "100vw" },
      }}
    >
      <div className="check_out_container">
        <div className="check_out_content">
          {data?.bottles?.map((item, index) => {
            if (["Dram Shop"].includes(item.type)) {
              return (
                <div key={index}>
                  <h3>Dram Shop</h3>
                  <ItemShop item={item} />
                </div>
              );
            }
            return (
              <div key={index}>
                <h3>Bottle</h3>
                <ItemShop item={item} />
              </div>
            );
          })}

          {data?.drams?.map((item, index) => (
            <div key={index} className="check_out_content__parentItem">
              <h3>Auction</h3>
              <h5>{formatDateTimeShort(item?.auction_end_date)}</h5>
              {item?.items?.map((item, index) => (
                <div key={index} className="check_out_content__item">
                  <div className="check_out_content__item__image">
                    <img src={item.image} alt="" />
                  </div>
                  <div className="check_out_content__item__info">
                    <div className="check_out_content__item__info__name">
                      {item.product_name}
                    </div>
                    <div className="check_out_content__item__info__price">
                      {formatCurrencySGD(item.price)}
                    </div>
                    <div className="check_out_content__item__info__quantity">
                      Quantity: {item.quantity}
                    </div>
                  </div>
                  <div className="check_out_content__item__total">
                    {formatCurrencySGD(item.price * item.quantity)}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div style={{ padding: "10px 0", lineHeight: "1.5" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Subtotal:</span>
              <span>{formatCurrencySGD(data?.subtotal)}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Auction Commission:</span>
              <span>{formatCurrencySGD(data?.buyerFee)}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Late Payment Fee:</span>
              <span>{formatCurrencySGD(data?.paymentFee)}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Delivery fee:</span>
              <span>{formatCurrencySGD(data?.totalShiping)}</span>
            </div>

            <br />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontFamily: "LatoBold",
              }}
            >
              <span style={{ fontFamily: "LatoBold" }}>
                Total amount payable:
              </span>
              <span style={{ fontFamily: "LatoBold" }}>
                {formatCurrencySGD(data?.total)}
              </span>
            </div>
          </div>

          {delivery?.country && (
            <div className="check_out__delivery">
              <div className="check_out__delivery__title">Delivery Details</div>
              <div className="check_out__delivery__info">
                <div className="check_out__delivery__info__name">
                  {delivery?.firstname && delivery?.firstname}{" "}
                  {delivery?.lastname && delivery?.lastname}{" "}
                </div>
                <div className="check_out__delivery__info__company__name">
                  {delivery?.companyname && delivery?.companyname}
                </div>
                <div className="check_out__delivery__info__address">
                  {["free-storage", "self"].includes(data?.selectShipping) ? (
                    <span>Storage</span>
                  ) : (
                    <span>
                      {`${delivery?.address ? delivery?.address + "," : ""} 
                      ${delivery?.city ? delivery?.city + "," : ""}
                      ${delivery?.state ? delivery?.state + "," : ""}
                      ${delivery?.country ? delivery?.country + "," : ""}
                      ${delivery?.postal_code ? delivery?.postal_code : ""}`}
                    </span>
                  )}
                </div>
                <div className="check_out__delivery__info__phone">
                  {delivery.phone && delivery.phone}
                </div>
              </div>
            </div>
          )}
        </div>

        <form id="omise-form" onSubmit={handleSubmit}>
          <div className="omise-payment-button">
            <button
              type="submit"
              disabled={loading}
              className="omise-checkout-button"
            >
              {loading ? "Processing..." : "Proceed to Payment"}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default OmisePayment;
